import { Paragraph, Stack } from '@mediahuis/chameleon-react';
import { FormikProps } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddressService } from '../../hooks/useAddressService';
import { useBrandGroupConfig } from '../../hooks/useBrandGroupConfig';
import {
  CountryConfig,
  RegistrationFlowField,
  RegistrationFlowOrchestration,
} from '../../types/apiConfig';
import { TRANSLATION_KEYS } from '../../types/validation';
import { CountryInput } from './CountryInput';
import { CountryTextButton } from './CountryTextButton';
import { FormikPostalCodeAndCityInput } from './FormikPostalCodeAndCityInput';
import { FormikStreetInput } from './FormikStreetInput';
import { FormikTextField } from './FormikTextField';
import { PostalCodeInput } from './PostalCodeInput';

type AddressFormValues = {
  country?: string;
  address: {
    street?: string;
    houseNumber?: string;
    box?: string;
    city?: string;
    postalCode?: string;
  };
};

interface FormikAddressFormProps<T extends AddressFormValues> {
  formikProps: FormikProps<T>;
  countryConfig: CountryConfig;
  orchestration: RegistrationFlowOrchestration;
  onCountryChange: (value: string) => void;
}

export const FormikAddressForm = <T extends AddressFormValues>({
  formikProps,
  countryConfig,
  orchestration,
  onCountryChange,
}: FormikAddressFormProps<T>) => {
  // Chameleon component styling
  const { t } = useTranslation();
  const { addressService } = useAddressService();
  const { values, setFieldValue } = formikProps as unknown as FormikProps<AddressFormValues>;
  const { brandConfig } = useBrandGroupConfig();
  const isDifferentCountry = useMemo((): boolean => {
    return brandConfig?.countryISO !== values.country;
  }, [brandConfig, values.country]);
  const [showCountryInput, setShowCountryInput] = useState(false);
  const onCountryTextButtonClick = useCallback(
    () => setShowCountryInput(true),
    [setShowCountryInput]
  );
  const shouldShowCountryInput = useMemo(() => {
    return isDifferentCountry || showCountryInput || brandConfig === undefined;
  }, [brandConfig, isDifferentCountry, showCountryInput]);

  useEffect(() => {
    if (
      !orchestration.visibleFields.includes(RegistrationFlowField.CITY) &&
      !orchestration.visibleFields.includes(RegistrationFlowField.POST_CODE) &&
      !orchestration.visibleFields.includes(RegistrationFlowField.STREET) &&
      !orchestration.visibleFields.includes(RegistrationFlowField.HOUSE_NUMBER) &&
      !orchestration.visibleFields.includes(RegistrationFlowField.BOX_NUMBER) &&
      orchestration.visibleFields.includes(RegistrationFlowField.COUNTRY)
    ) {
      setShowCountryInput(true);
    }
  }, [orchestration.visibleFields]);

  useEffect(() => {
    if (
      countryConfig.resolveDutchAddress &&
      orchestration.visibleFields.includes(RegistrationFlowField.STREET) &&
      values.address.postalCode &&
      values.address.houseNumber
    ) {
      addressService
        .resolveDutchAddress({
          postCode: values.address.postalCode,
          houseNumber: parseInt(values.address.houseNumber),
        })
        .then((result) => {
          const street = result.Address?.Street.Name || '';
          setFieldValue('address.street', street);
        });
    }
  }, [
    addressService,
    countryConfig.resolveDutchAddress,
    values.address.houseNumber,
    values.address.postalCode,
    setFieldValue,
    orchestration.visibleFields,
  ]);

  return (
    <>
      {shouldShowCountryInput && (
        <Stack className="mb-5">
          <CountryInput
            label={t('register.address.country')}
            onChange={onCountryChange}
            style={{ width: '100%' }}
            formik={formikProps}
          />
        </Stack>
      )}
      {[RegistrationFlowField.POST_CODE, RegistrationFlowField.CITY].some((f) =>
        orchestration.visibleFields.includes(f)
      ) && (
        <div style={{ width: '100%' }}>
          {orchestration.visibleFields.includes(RegistrationFlowField.CITY) ? (
            <FormikPostalCodeAndCityInput
              countryISO={values.country}
              formik={formikProps}
              style={{ width: '100%' }}
              autoComplete={countryConfig.autoComplete}
            />
          ) : (
            <PostalCodeInput
              countryISO={values.country}
              formik={formikProps}
              label={t('register.address.postalCode')}
              value={values.address.postalCode || ''}
              autoComplete={countryConfig.autoComplete}
            />
          )}
          {!(isDifferentCountry || showCountryInput) && (
            <div className="mt-1">
              <CountryTextButton onClick={onCountryTextButtonClick} />
            </div>
          )}
          {countryConfig.resolveDutchAddress &&
            orchestration.visibleFields.includes(RegistrationFlowField.STREET) && (
              <div className="mt-1">
                <Paragraph color="grey60" data-cy="addressStreetAutoComplete">
                  {values.address.street}
                </Paragraph>
              </div>
            )}
        </div>
      )}
      {orchestration.visibleFields.includes(RegistrationFlowField.STREET) &&
        !countryConfig.resolveDutchAddress && (
          <Stack className="mb-5">
            <FormikStreetInput
              label={t('register.address.street')}
              countryISO={values.country}
              formik={formikProps}
              autoComplete={countryConfig.autoComplete}
              style={{ width: '100%' }}
            />
          </Stack>
        )}
      {[RegistrationFlowField.HOUSE_NUMBER, RegistrationFlowField.BOX_NUMBER].every((f) =>
        orchestration.visibleFields.includes(f)
      ) && (
        <Stack
          className="naming__inputs mb-5 mt-0 g-7"
          flexDirection={{
            sm: 'row',
            md: 'row',
            lg: 'row',
            xl: 'row',
          }}
          flexWrap={'nowrap'}>
          <FormikTextField
            name="address.houseNumber"
            id="address.houseNumber"
            data-cy="addressHouseNumber"
            label={
              values.country === 'NL'
                ? t('register.address.numberNL')
                : t('register.address.number')
            }
            formikProps={formikProps}
          />
          <FormikTextField
            name="address.box"
            id="address.box"
            data-cy="addressBox"
            label={
              values.country === 'NL' ? t('register.address.boxNL') : t('register.address.box')
            }
            errorMessage={t(TRANSLATION_KEYS.INVALID_BOX_FORMAT)}
            formikProps={formikProps}
          />
        </Stack>
      )}
    </>
  );
};
